import italyRegions from "./data/italy-regions.json";
import { getVendorRoute } from "../utils/vendorRoute";

const DEFAULT_OPTIONS = {
  width: 540,
  height: 720,
  padding: 36,
  fillColor: '#515151',
  strokeColor: '#8a8a8a',
  strokeWidth: 1.2,
  glowColor: 'rgba(0, 0, 0, 0.55)',
  markerColor: '#ff5f5f',
  markerInnerColor: '#ffffff',
  markerSize: 14,
  markers: [],
  fallbackMarkerCount: 3,
  markerPopoverBackground: 'rgba(15, 23, 42, 0.95)',
  markerPopoverColor: '#f4f7fb',
};

const DATASETS = {
  italy: italyRegions,
};

const DEG2RAD = Math.PI / 180;

const mercatorProject = ([longitude, latitude]) => {
  const lambda = longitude * DEG2RAD;
  const phi = latitude * DEG2RAD;
  const x = lambda;
  const y = Math.log(Math.tan(Math.PI / 4 + phi / 2));
  return [x, y];
};

const getRings = (geometry = {}) => {
  const { type, coordinates = [] } = geometry;
  if (type === 'Polygon') {
    return coordinates;
  }
  if (type === 'MultiPolygon') {
    return coordinates.reduce((acc, polygon) => acc.concat(polygon), []);
  }
  return [];
};

const sanitizePath = (segments) => segments.replace(/\s+/g, ' ').trim();

// MODIFY THE normalizeMarker FUNCTION TO PASS THROUGH vendorData
const normalizeMarker = (marker, index) => {
  if (!marker) return null;
  if (Array.isArray(marker) && marker.length >= 2) {
    return {
      coordinates: marker,
      label: marker.label || `marker-${index}`,
      info: marker.info || marker.description || marker.value || '',
      // --- COPY OTHER PROPS FROM ARRAY OBJECT ---
      ...Object.fromEntries(Object.entries(marker).filter(([key]) => isNaN(key) && key !== '0' && key !== '1'))
      // --- END COPY ---
    };
  }
  const coordinates =
    marker.coordinates ||
    marker.coord ||
    marker.location ||
    marker.loc?.coordinates ||
    (typeof marker.longitude === 'number' && typeof marker.latitude === 'number'
      ? [marker.longitude, marker.latitude]
      : null) ||
    (typeof marker.lon === 'number' && typeof marker.lat === 'number'
      ? [marker.lon, marker.lat]
      : null) ||
    (typeof marker.lng === 'number' && typeof marker.lat === 'number'
      ? [marker.lng, marker.lat]
      : null);
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }
  const [lon, lat] = coordinates.map((value) => Number(value));
  if (Number.isNaN(lon) || Number.isNaN(lat)) {
    return null;
  }
  // --- COPY ALL PROPERTIES FROM THE ORIGINAL MARKER OBJECT, including vendorData ---
  return {
    coordinates: [lon, lat],
    label: marker.label || marker.value || marker.name || `marker-${index}`,
    info:
      marker.info ||
      marker.description ||
      marker.desc ||
      marker.value ||
      marker.address ||
      '',
    // Spread the original marker object to capture any extra properties like vendorData
    ...marker
  };
  // --- END COPY ---
};

export default class Mapgeo {
  constructor(container, options = {}) {
    if (!container) {
      throw new Error('Mapgeo requires a valid container element');
    }

    this.container = container;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.variant = options.variant || 'italy';
    this.markerDetails = [];
    this.markerEventHandlers = [];
    this.popoverEl = null;
    this.activeMarkerIndex = null;
    this.handleDocumentClick = this.hidePopover.bind(this);
    this.render();
  }

  render() {
    const dataset = DATASETS[this.variant];

    if (!dataset || !dataset.features || dataset.features.length === 0) {
      this.container.innerHTML = '';
      return;
    }

    const {
      width,
      height,
      padding,
      fillColor,
      strokeColor,
      strokeWidth,
      glowColor,
      markerColor,
      markerInnerColor,
      markerSize,
      markers,
      fallbackMarkerCount,
      markerPopoverBackground,
      markerPopoverColor,
    } = this.options;

    const glowId = `mapgeo-glow-${this.variant}-${Math.random().toString(36).slice(2, 8)}`;
    const projectedFeatures = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    dataset.features.forEach((feature, index) => {
      const rings = getRings(feature.geometry).map((ring) => {
        const projectedRing = ring.map((coordinate) => {
          const [x, y] = mercatorProject(coordinate);
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
          return [x, y];
        });
        return projectedRing;
      });

      const baseRing = rings[0] || [];
      const centroid =
        baseRing.length > 0
          ? baseRing.reduce(
              (acc, [x, y]) => {
                acc[0] += x;
                acc[1] += y;
                return acc;
              },
              [0, 0]
            )
          : null;

      projectedFeatures.push({
        id: feature.properties?.name || `region-${index}`,
        rings,
        centroid: centroid
          ? [centroid[0] / baseRing.length, centroid[1] / baseRing.length]
          : null,
      });
    });

    const innerWidth = Math.max(width - padding * 2, 1);
    const innerHeight = Math.max(height - padding * 2, 1);
    const spanX = Math.max(maxX - minX, 0.0001);
    const spanY = Math.max(maxY - minY, 0.0001);
    const scale = Math.min(innerWidth / spanX, innerHeight / spanY);
    const offsetX = padding + (innerWidth - spanX * scale) / 2;
    const offsetY = padding + (innerHeight - spanY * scale) / 2;

    const projectPointRaw = ([x, y]) => {
      const scaledX = (x - minX) * scale + offsetX;
      const scaledY = (y - minY) * scale + offsetY;
      return [scaledX, height - scaledY];
    };

    const projectPoint = (point) => {
      const [x, y] = projectPointRaw(point);
      return [x.toFixed(2), y.toFixed(2)];
    };

    const svgPaths = projectedFeatures
      .map(({ id, rings }) => {
        if (!rings.length) {
          return '';
        }

        const pathData = rings
          .map((ring) => {
            if (!ring.length) {
              return '';
            }

            return ring
              .map((point, index) => {
                const [x, y] = projectPoint(point);
                const command = index === 0 ? 'M' : 'L';
                return `${command}${x} ${y}`;
              })
              .join(' ')
              .concat(' Z');
          })
          .join(' ');

        return `<path d="${sanitizePath(pathData)}" data-region="${id}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />`;
      })
      .join('');

    const providedMarkerPoints = (Array.isArray(markers) ? markers : [])
          .map(normalizeMarker) // This still only outputs { coordinates, label, info, ...original_props }
          .filter(Boolean)
          .map((normalizedMarker, index) => {
            const projected = mercatorProject(normalizedMarker.coordinates);
            const [x, y] = projectPointRaw(projected);
            // Spread the normalizedMarker (which now includes vendorId if normalizeMarker is fixed)
            // onto the new object being created for markerElementsSource
            return {
              id: normalizedMarker.label || `marker-${index}`,
              x,
              y,
              label: normalizedMarker.label || `marker-${index}`,
              description: normalizedMarker.info || normalizedMarker.value || '',
              // --- SPREAD THE NORMALIZED MARKER PROPERTIES (LIKE vendorId) ---
              ...normalizedMarker
              // --- END SPREAD ---
            };
          });

    const availableMarkers = projectedFeatures
      .filter((feature) => Array.isArray(feature.centroid))
      .map((feature) => {
        const [x, y] = projectPointRaw(feature.centroid);
        return { id: feature.id, x, y, label: feature.id, description: '' };
      });

    const fallbackMarkers =
      providedMarkerPoints.length === 0
        ? availableMarkers
            .sort(() => Math.random() - 0.5)
            .slice(0, Math.max(0, Math.min(fallbackMarkerCount, availableMarkers.length)))
        : [];

    const markerElementsSource =
      providedMarkerPoints.length > 0 ? providedMarkerPoints : fallbackMarkers;

    const glowFilter = `
      <filter id="${glowId}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="20" stdDeviation="15" flood-color="${glowColor}" />
      </filter>
    `;
    this.markerDetails = markerElementsSource;

    const markerElements = markerElementsSource
      .map(({ x, y }, index) => {
        const size = markerSize;
        const circleY = -size * 0.4;
        const pinPath = `
          M0 ${-size * 0.3}
          C ${size * 0.55} ${-size * 0.3} ${size * 0.55} ${size * 0.55} 0 ${size}
          C ${-size * 0.55} ${size * 0.55} ${-size * 0.55} ${-size * 0.3} 0 ${-size * 0.3}
          Z
        `;

        return `
          <g class="mapgeo-marker" data-marker-index="${index}" transform="translate(${x.toFixed(2)} ${y.toFixed(2)})">
            <path d="${sanitizePath(pinPath)}" fill="${markerColor}" fill-opacity="0.95" />
            <circle cx="0" cy="${circleY.toFixed(2)}" r="${(size * 0.22).toFixed(2)}" fill="${markerInnerColor}" />
          </g>
        `;
      })
      .join('');

    this.container.innerHTML = `
      <svg
        viewBox="0 0 ${width} ${height}"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Static map of Italy"
      >
        <defs>
          ${glowFilter}
        </defs>
        <g filter="url(#${glowId})">
          ${svgPaths}
          ${markerElements}
        </g>
      </svg>
    `;

    this.attachMarkerInteractions(markerPopoverBackground, markerPopoverColor);
  }

  destroy() {
    this.hidePopover();
    this.detachMarkerInteractions();
    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  attachMarkerInteractions(background, color) {
    this.detachMarkerInteractions();
    if (!this.container) {
      return;
    }

    if (!this.container.style.position || this.container.style.position === 'static') {
      this.container.style.position = 'relative';
    }

    const markers = this.container.querySelectorAll('.mapgeo-marker');

    markers.forEach((markerNode) => {
      const handler = (event) => {
        event.stopPropagation();
        const markerIndex = Number(markerNode.getAttribute('data-marker-index'));
        const details = this.markerDetails[markerIndex];
        if (!details) {
          return;
        }

        if (this.activeMarkerIndex === markerIndex) {
          this.hidePopover();
          return;
        }

        this.showPopover(event.currentTarget, details, background, color, markerIndex);
      };
      markerNode.style.cursor = 'pointer';
      markerNode.addEventListener('click', handler);
      this.markerEventHandlers.push({ markerNode, handler });
    });

    document.addEventListener('click', this.handleDocumentClick, false);
  }

  detachMarkerInteractions() {
    this.markerEventHandlers.forEach(({ markerNode, handler }) => {
      markerNode.removeEventListener('click', handler);
    });
    this.markerEventHandlers = [];
    document.removeEventListener('click', this.handleDocumentClick, false);
  }

  createPopover(background, color) {
    if (this.popoverEl) {
      return this.popoverEl;
    }

    const popover = document.createElement('div');
    popover.className = 'mapgeo-popover';
    Object.assign(popover.style, {
      position: 'absolute',
      minWidth: '180px',
      maxWidth: '260px',
      padding: '12px 16px',
      borderRadius: '12px',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
      pointerEvents: 'none',
      opacity: '0',
      transform: 'translate(-50%, -120%)',
      transition: 'opacity 0.2s ease',
      zIndex: '5',
      background,
      color,
      fontSize: '0.9rem',
      lineHeight: '1.4',
    });

    this.popoverEl = popover;
    this.container.appendChild(popover);
    return popover;
  }


// MODIFY THE showPopover FUNCTION TO USE vendorData AND getVendorRoute LOGIC
// Find the showPopover function and replace its content:
showPopover(markerNode, details, background, color, markerIndex) {
  const popover = this.createPopover(background, color);
  if (!popover) return;

  let linkHtml = `<span>${details.label || ''}</span>`;

  if (details.vendorData) {
    const route = getVendorRoute(details.vendorData);
    if (route?.name === 'VendorDetail' && route?.params?.vendorId) {
      const url = `/vendors/${route.params.vendorId}`;
      linkHtml = `<a href="${url}" style="color: inherit; text-decoration: underline;">${details.label || ''}</a>`;
    } else if (route?.name === 'Vendors') {
      linkHtml = `<a href="/vendors" style="color: inherit; text-decoration: underline;">${details.label || ''}</a>`;
    }
  }

  popover.innerHTML = `
    <strong style="display:block; font-size:1rem; margin-bottom:4px;">
      ${linkHtml}
    </strong>
    ${
      details.description
        ? `<span style="font-size:0.85rem; opacity:0.85;">${details.description}</span>`
        : ''
    }
  `;

  const markerRect = markerNode.getBoundingClientRect();
  const containerRect = this.container.getBoundingClientRect();
  const top = markerRect.top - containerRect.top;
  const left = markerRect.left - containerRect.left + markerRect.width / 2;
  popover.style.left = `${left}px`;
  popover.style.top = `${top}px`;
  popover.style.opacity = '1';
  popover.style.pointerEvents = 'auto';
  this.activeMarkerIndex = markerIndex;
}

  hidePopover() {
    if (this.popoverEl) {
      this.popoverEl.style.opacity = '0';
      this.popoverEl.style.pointerEvents = 'none';
    }
    this.activeMarkerIndex = null;
  }
}
